import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { GridSortModel } from '@mui/x-data-grid'
import type { SelectedFilters } from '@/models/filters'

export interface TableUrlState {
  pageIndex: number
  pageSize: number
  searchTerm: string
  sortModel: GridSortModel
  filters: SelectedFilters
}

const DEFAULT_PAGE_SIZE = 10
// Use Set instead of Array for O(1) lookups
const RESERVED_PARAMS = new Set(['p', 'size', 'q', 'sort', 'order'])

/**
 * Parse filters from URL search params
 */
const parseFiltersFromParams = (
  searchParams: URLSearchParams,
): SelectedFilters => {
  const filters: SelectedFilters = {}

  for (const key of searchParams.keys()) {
    if (!RESERVED_PARAMS.has(key)) {
      if (!filters[key]) {
        filters[key] = {}
      }
      const allValues = searchParams.getAll(key)
      for (const v of allValues) {
        filters[key][v] = v
      }
    }
  }
  return filters
}

/**
 * Remove all filter params from URLSearchParams
 */
const removeFilterParams = (params: URLSearchParams): void => {
  for (const key of Array.from(params.keys())) {
    if (!RESERVED_PARAMS.has(key)) {
      params.delete(key)
    }
  }
}

/**
 * Add filter params to URLSearchParams
 */
const addFilterParams = (
  params: URLSearchParams,
  filters: SelectedFilters,
): void => {
  for (const [groupId, options] of Object.entries(filters)) {
    for (const optionKey of Object.keys(options)) {
      params.append(groupId, optionKey)
    }
  }
}

/**
 * Update URLSearchParams with new table state
 */
const updateParams = (
  prev: URLSearchParams,
  newState: Partial<TableUrlState>,
): URLSearchParams => {
  const params = new URLSearchParams(prev)

  // Update pagination
  if (newState.pageIndex !== undefined) {
    params.set('p', String(newState.pageIndex + 1))
  }
  if (newState.pageSize !== undefined) {
    params.set('size', String(newState.pageSize))
  }

  // Update search
  if (newState.searchTerm !== undefined) {
    if (newState.searchTerm) {
      params.set('q', newState.searchTerm)
    } else {
      params.delete('q')
    }
  }

  // Update sort
  if (newState.sortModel !== undefined) {
    if (newState.sortModel.length > 0) {
      const sort = newState.sortModel[0]
      params.set('sort', sort.field)
      params.set('order', sort.sort ?? 'asc')
    } else {
      params.delete('sort')
      params.delete('order')
    }
  }

  // Update filters
  if (newState.filters !== undefined) {
    removeFilterParams(params)
    addFilterParams(params, newState.filters)
  }

  return params
}

/**
 * Hook to manage table state via URL search params
 * Works with React Router v6
 */
export const useTableUrlState = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  // Read current state from URL
  const urlState = useMemo((): TableUrlState => {
    const pageIndex = Math.max(0, Number(searchParams.get('p') ?? 1) - 1)
    const pageSize = Number(searchParams.get('size')) || DEFAULT_PAGE_SIZE
    const searchTerm = searchParams.get('q') ?? ''

    // Parse sort
    const sortField = searchParams.get('sort')
    const sortOrder = searchParams.get('order') as 'asc' | 'desc' | null
    const sortModel: GridSortModel = sortField
      ? [{ field: sortField, sort: sortOrder ?? 'asc' }]
      : []

    // Parse filters
    const filters = parseFiltersFromParams(searchParams)

    return {
      pageIndex,
      pageSize,
      searchTerm,
      sortModel,
      filters,
    }
  }, [searchParams])

  // Update URL with new state
  const updateUrlState = useCallback(
    (newState: Partial<TableUrlState>) => {
      setSearchParams((prev) => updateParams(prev, newState), { replace: true })
    },
    [setSearchParams],
  )

  return {
    urlState,
    updateUrlState,
  }
}
